import React, { useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    FormControl,
    Select,
    MenuItem,
    SelectChangeEvent,
    FormHelperText,
} from '@mui/material';
import { IndianStates } from '../../../../Constants/Arrays';

interface StateDropdownProps {
    label: string;
    value: string;
    error?: string;
    required?: boolean;
    onChange: (name: string, value: string) => void;
    onBlur: (name: string) => void;
    normalizeOnMount?: boolean;
}

const StateDropdown: React.FC<StateDropdownProps> = ({
    label,
    value,
    error,
    required = false,
    onChange,
    onBlur,
    normalizeOnMount = true,
}) => {
    console.log("Rendering StateDropdown with value:", value);
    // Sort states alphabetically by label
    const sortedStates = [...IndianStates].sort((a, b) => a.label.localeCompare(b.label));
    console.log("Sorted states:", sortedStates);

    // helper maps for fast lookup
    const valueToLabelMap = useMemo(() => {
        const m = new Map<string, string>();
        for (const s of sortedStates) m.set(s.value, s.label);
        return m;
    }, [sortedStates]);

    const labelToValueMap = useMemo(() => {
        const m = new Map<string, string>();
        for (const s of sortedStates) m.set(s.label.toLowerCase().trim(), s.value);
        return m;
    }, [sortedStates]);

    // Determine what should be used as the Select value (the *code*). If caller passed
    // label (e.g. "Madhya Pradesh"), we convert to its code ("MP") for proper selection.
    const selectedCode = useMemo<string>(() => {
        if (!value) return "";
        // If it's already a known code, return it
        if (valueToLabelMap.has(value)) return value;
        // If passed label, match case-insensitive
        const maybe = labelToValueMap.get(value.toLowerCase().trim());
        if (maybe) return maybe;
        // fallback: return empty so placeholder shows
        return "";
    }, [value, valueToLabelMap, labelToValueMap]);

    // If parent passed label and normalizeOnMount is true, notify parent of normalized code.
    useEffect(() => {
        if (!normalizeOnMount) return;
        if (!value) return;

        // If parent already passed a code, nothing to do.
        if (valueToLabelMap.has(value)) return;

        // If it's a label that we can map, call onChange to normalize
        const mapped = labelToValueMap.get(value.toLowerCase().trim());
        console.log("Normalizing state value on mount:", value, "->", mapped);
        if (mapped && mapped !== value) {
            // notify parent to update value with code
            onChange("state", mapped);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, labelToValueMap, valueToLabelMap, normalizeOnMount]);

    // const handleChange = (event: SelectChangeEvent<string>) => {
    //     const selectedValue = event.target.value;
    //     debugger
    //     console.log("StateDropdown selected value:", selectedValue);
    //     onChange('state', selectedValue);
    // };

    const handleChange = (event: SelectChangeEvent<string>) => {
        const selectedCode = event.target.value;

        // Find the full name (label) for the selected code
        // const selectedState = sortedStates.find(s => s.value === selectedCode);

        // const fullName = selectedState ? selectedState.label : selectedCode;
        // console.log("StateDropdown selected full name:", fullName);

        // Send full name ("Madhya Pradesh") to parent instead of code ("MP")
        onChange('state', selectedCode);
    };

    const handleBlur = () => {
        onBlur('state');
    };




    return (
        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
            <Typography
                variant="subtitle1"
                sx={{
                    minWidth: { xs: '120px', sm: '200px' },
                    flexShrink: 0,
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#212529',
                }}
            >
                {label}
                {required && (
                    <Typography component="span" sx={{ color: '#dc3545', ml: 0.5 }}>
                        *
                    </Typography>
                )}
            </Typography>

            <Box sx={{ flex: 1 }}>
                <FormControl fullWidth error={!!error}>
                    <Select
                        value={selectedCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        displayEmpty
                        sx={{
                            height: '48px',
                            borderRadius: '8px',
                            backgroundColor: '#fff',
                            fontSize: '16px',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: error ? '#dc3545' : '#dee2e6',
                                borderWidth: '1px',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: error ? '#dc3545' : '#007bff',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: error ? '#dc3545' : '#007bff',
                                borderWidth: '2px',
                            },
                            '& .MuiSelect-select': {
                                padding: '12px 16px',
                                fontSize: '16px',
                            },
                        }}
                    >
                        <MenuItem value="" disabled>
                            <Typography sx={{ color: '#6c757d', fontSize: '16px' }}>
                                Select your state
                            </Typography>
                        </MenuItem>
                        {sortedStates.map((state) => (
                            <MenuItem
                                key={state.id}
                                value={state.value}
                                sx={{
                                    fontSize: '16px',
                                    '&:hover': {
                                        backgroundColor: '#f8f9fa',
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: '#e7f1ff',
                                        '&:hover': {
                                            backgroundColor: '#d1ecf1',
                                        },
                                    },
                                }}
                            >
                                {state.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {error && (
                        <FormHelperText sx={{ fontSize: '12px', color: '#dc3545', ml: 0, mt: '4px' }}>
                            {error}
                        </FormHelperText>
                    )}
                </FormControl>
            </Box>
        </Box>
    );
};

export default StateDropdown;